const EXPORT_NODE_ID = 'data-qc-pdf-export-id';
const A4_WIDTH_PT = 595.28;
const A4_HEIGHT_PT = 841.89;
const A4_WIDTH_PX_96DPI = 794;
const PDF_MARGIN_PT = 24;
const PDF_IMAGE_FORMAT = 'JPEG';
const PDF_IMAGE_MIME_TYPE = `image/${PDF_IMAGE_FORMAT.toLowerCase()}`;
const PDF_IMAGE_QUALITY = 0.9;
const CANVAS_SCALE = 2;
const MIN_USEFUL_PAGE_HEIGHT_RATIO = 0.55;
const PDF_BACKGROUND_COLOR = '#ffffff';
const PDF_PAGE_CONTENT_WIDTH_PT = A4_WIDTH_PT - PDF_MARGIN_PT * 2;
const PDF_PAGE_CONTENT_HEIGHT_PT = A4_HEIGHT_PT - PDF_MARGIN_PT * 2;
const activeExports = new WeakSet<HTMLElement>();

type Html2Canvas = typeof import('html2canvas-pro').default;
type JsPDFConstructor = typeof import('jspdf').default;

const loadPdfDependencies = async (): Promise<{
  html2canvas: Html2Canvas;
  JsPDF: JsPDFConstructor;
}> => {
  const [{ default: html2canvas }, { default: JsPDF }] = await Promise.all([
    import('html2canvas-pro'),
    import('jspdf'),
  ]);

  return { html2canvas, JsPDF };
};

const prepareExportClone = (clonedRoot: HTMLElement) => {
  const root = clonedRoot;
  root.style.width = `${A4_WIDTH_PX_96DPI}px`;
  root.style.maxWidth = `${A4_WIDTH_PX_96DPI}px`;
  root.style.height = 'auto';
  root.style.maxHeight = 'none';
  root.style.overflow = 'visible';
  root.style.backgroundColor = PDF_BACKGROUND_COLOR;

  root.querySelectorAll<HTMLElement>('.truncate').forEach(node => {
    node.style.overflow = 'visible';
    node.style.textOverflow = 'clip';
    node.style.whiteSpace = 'normal';
  });
};

const getCanvasPageBreaks = (canvas: HTMLCanvasElement, sourceRoot: HTMLElement) => {
  const rootRect = sourceRoot.getBoundingClientRect();
  if (rootRect.width <= 0) return [];

  const cssToCanvasScale = canvas.width / rootRect.width;
  const cssPageHeight = (PDF_PAGE_CONTENT_HEIGHT_PT / PDF_PAGE_CONTENT_WIDTH_PT) * rootRect.width;
  const minUsefulPageHeight = cssPageHeight * MIN_USEFUL_PAGE_HEIGHT_RATIO;
  const breakableBlocks = Array.from(
    sourceRoot.querySelectorAll<HTMLElement>('[data-qc-pdf-block]'),
  )
    .map(node => {
      const rect = node.getBoundingClientRect();
      return {
        bottom: rect.bottom - rootRect.top,
        top: rect.top - rootRect.top,
      };
    })
    .filter(block => block.bottom > 0 && block.top < rootRect.height)
    .sort((a, b) => a.top - b.top);

  const breaks: number[] = [];
  let pageStart = 0;

  while (pageStart + cssPageHeight < rootRect.height) {
    const targetBreak = pageStart + cssPageHeight;
    const splitBlockCandidates = breakableBlocks.filter(
      block =>
        block.top < targetBreak &&
        block.bottom > targetBreak &&
        block.top > pageStart &&
        block.top - pageStart >= minUsefulPageHeight,
    );
    const splitBlock = splitBlockCandidates[splitBlockCandidates.length - 1];
    const nextBreak = splitBlock ? splitBlock.top : targetBreak;

    if (nextBreak <= pageStart) break;
    breaks.push(Math.round(nextBreak * cssToCanvasScale));
    pageStart = nextBreak;
  }

  return breaks;
};

const drawCanvasPagesToPortraitPdf = (
  canvas: HTMLCanvasElement,
  filename: string,
  JsPDF: JsPDFConstructor,
  sourceRoot: HTMLElement,
) => {
  const doc = new JsPDF({ compress: true, orientation: 'portrait', unit: 'pt', format: 'a4' });
  const imageWidth = A4_WIDTH_PT - PDF_MARGIN_PT * 2;
  const imageHeight = A4_HEIGHT_PT - PDF_MARGIN_PT * 2;
  const sourcePageHeight = Math.floor((imageHeight / imageWidth) * canvas.width);
  const breakPoints = getCanvasPageBreaks(canvas, sourceRoot);
  const pageStarts = [0, ...breakPoints];

  for (let index = 0; index < pageStarts.length; index += 1) {
    const sourceY = pageStarts[index];
    const nextBreak = pageStarts[index + 1] ?? canvas.height;
    const sliceHeight = Math.min(nextBreak - sourceY, sourcePageHeight, canvas.height - sourceY);
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = sourcePageHeight;

    const context = pageCanvas.getContext('2d');
    if (!context) {
      throw new Error('Unable to prepare evaluation PDF page image.');
    }

    context.fillStyle = PDF_BACKGROUND_COLOR;
    context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    context.drawImage(
      canvas,
      0,
      sourceY,
      canvas.width,
      sliceHeight,
      0,
      0,
      canvas.width,
      sliceHeight,
    );

    let image: string;
    try {
      image = pageCanvas.toDataURL(PDF_IMAGE_MIME_TYPE, PDF_IMAGE_QUALITY);
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'unknown canvas serialization error';
      throw new Error(`Unable to prepare evaluation PDF image: ${reason}`);
    }

    if (index > 0) {
      doc.addPage();
    }

    doc.addImage(image, PDF_IMAGE_FORMAT, PDF_MARGIN_PT, PDF_MARGIN_PT, imageWidth, imageHeight);
  }

  doc.save(filename);
};

export const downloadQcEvaluationPdf = async (element: HTMLElement, filename: string) => {
  if (activeExports.has(element)) {
    throw new Error('A PDF export is already in progress for this evaluation.');
  }

  activeExports.add(element);
  const exportId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  try {
    await document.fonts?.ready;
    const { html2canvas, JsPDF } = await loadPdfDependencies();
    element.setAttribute(EXPORT_NODE_ID, exportId);

    const canvas = await html2canvas(element, {
      backgroundColor: PDF_BACKGROUND_COLOR,
      scale: CANVAS_SCALE,
      useCORS: true,
      onclone: clonedDocument => {
        const clonedRoot = clonedDocument.querySelector<HTMLElement>(
          `[${EXPORT_NODE_ID}="${exportId}"]`,
        );

        if (clonedRoot) {
          prepareExportClone(clonedRoot);
          clonedRoot.removeAttribute(EXPORT_NODE_ID);
        }
      },
    });

    drawCanvasPagesToPortraitPdf(canvas, filename, JsPDF, element);
  } finally {
    if (element.getAttribute(EXPORT_NODE_ID) === exportId) {
      element.removeAttribute(EXPORT_NODE_ID);
    }
    activeExports.delete(element);
  }
};
